import type { TeamLivePosition } from "@/types";

export interface TrailPoint {
  lat: number;
  lng: number;
  /** Epoch ms — when the fix was captured. */
  at: number;
}

/** Per member: gap-separated segments (oldest first), keyed by userId. */
export type TrailState = Record<string, TrailPoint[][]>;

export const TRAIL_MIN_MOVE_M = 1;
export const TRAIL_GAP_MS = 10_000;
/** 300 km/h in m/s — faster than this between fixes is a glitch/teleport. */
export const TRAIL_MAX_SPEED_MPS = 83;
export const TRAIL_MAX_POINTS_PER_MEMBER = 2_000;

export function trailStorageKey(teamId: string): string {
  return `teamlive-trail-${teamId}`;
}

export function distanceMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6_371_000;
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h =
    sinLat * sinLat +
    Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * sinLng * sinLng;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function evictToCap(segments: TrailPoint[][]): TrailPoint[][] {
  let count = segments.reduce((n, seg) => n + seg.length, 0);
  const next = segments.map((seg) => [...seg]);
  let i = 0;
  while (count > TRAIL_MAX_POINTS_PER_MEMBER && i < next.length) {
    const drop = Math.min(count - TRAIL_MAX_POINTS_PER_MEMBER, next[i].length);
    next[i] = next[i].slice(drop);
    count -= drop;
    i++;
  }
  return next.filter((seg) => seg.length > 0);
}

export function mergePositionsIntoTrail(
  state: TrailState,
  positions: TeamLivePosition[],
): TrailState {
  const next: TrailState = { ...state };
  for (const p of positions) {
    const at = Date.parse(p.capturedAt);
    if (!Number.isFinite(at) || !Number.isFinite(p.lat) || !Number.isFinite(p.lng)) {
      continue;
    }
    const point: TrailPoint = { lat: p.lat, lng: p.lng, at };
    const segments = next[p.member.userId]?.map((seg) => [...seg]) ?? [];
    const lastSeg = segments[segments.length - 1];
    const last = lastSeg?.[lastSeg.length - 1];

    if (!last) {
      next[p.member.userId] = [[point]];
      continue;
    }

    const dtMs = at - last.at;
    if (dtMs <= 0) continue; // duplicate or out-of-order poll — idempotent no-op

    const dist = distanceMeters(last, point);
    const speedMps = dist / (dtMs / 1_000);
    if (dtMs > TRAIL_GAP_MS || speedMps > TRAIL_MAX_SPEED_MPS) {
      segments.push([point]); // gap/teleport — never bridge with a line
    } else if (dist < TRAIL_MIN_MOVE_M) {
      continue; // stationary GPS jitter
    } else {
      lastSeg.push(point);
    }
    next[p.member.userId] = evictToCap(segments);
  }
  return next;
}

export function flattenSegments(
  state: TrailState,
): Array<Array<{ lat: number; lng: number }>> {
  return Object.values(state).flatMap((segments) =>
    segments.map((seg) => seg.map(({ lat, lng }) => ({ lat, lng }))),
  );
}

function isValidState(value: unknown): value is TrailState {
  if (typeof value !== "object" || value === null) return false;
  for (const segments of Object.values(value as Record<string, unknown>)) {
    if (!Array.isArray(segments)) return false;
    for (const seg of segments) {
      if (!Array.isArray(seg)) return false;
      for (const p of seg) {
        if (
          typeof p !== "object" ||
          p === null ||
          !Number.isFinite((p as TrailPoint).lat) ||
          !Number.isFinite((p as TrailPoint).lng) ||
          !Number.isFinite((p as TrailPoint).at)
        ) {
          return false;
        }
      }
    }
  }
  return true;
}

export function serializeTrail(state: TrailState): string {
  return JSON.stringify(state);
}

export function parseTrail(raw: string): TrailState {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isValidState(parsed)) return {};
    // Re-cap on restore — a payload written by an older/different cap must not
    // blow past the limit.
    const state: TrailState = {};
    for (const [userId, segments] of Object.entries(parsed)) {
      state[userId] = evictToCap(segments);
    }
    return state;
  } catch {
    return {};
  }
}
